'use client';

import ChevronDown from '@/assets/svgs/chevron-down.svg';
import { useTestContactLists } from '@/containers/ContactLists/hooks/useTestContactLists';
import { TestContactList } from '@/containers/ContactLists/types';
import InfoAlert from '@/shared/components/InfoAlert/InfoAlert';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';

interface TestListSelectorProps {
  identityId: string;
  accountId: string;
  selected: TestContactList | null;
  onSelect: (list: TestContactList) => void;
}

const TestListSelector = ({
  identityId,
  accountId,
  selected,
  onSelect,
}: TestListSelectorProps) => {
  const { testLists, isLoading } = useTestContactLists({
    identityId,
    accountId,
  });
  const dropdownRef = useRef<HTMLUListElement>(null);

  // Auto-select the first list if none is selected and lists are available
  useEffect(() => {
    if (!selected && testLists.length > 0) {
      onSelect(testLists[0]);
    }
  }, [testLists, selected, onSelect]);

  const handleSelectTestList = (list: TestContactList) => {
    onSelect(list);
    // Try to blur the currently focused element to close the dropdown
    if (dropdownRef.current) {
      dropdownRef.current.blur();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="font-medium">Select a test list</label>

      {isLoading ? (
        <div className="flex items-center">
          <span className="loading loading-spinner loading-sm"></span>
          <span className="ml-2">Loading test lists...</span>
        </div>
      ) : testLists.length > 0 ? (
        <div className="dropdown w-full">
          <div
            tabIndex={0}
            role="button"
            className={classNames(
              'btn btn-outline flex w-full justify-between',
              selected ? 'text-black' : 'text-gray-500'
            )}>
            {selected ? selected.list_name : 'Select a test list'}
            <ChevronDown className="text-neutral-default" />
          </div>
          <ul
            tabIndex={0}
            className="menu dropdown-content rounded-box bg-base-100 z-[1] w-full p-2 shadow"
            ref={dropdownRef}>
            {testLists.map((list) => (
              <li key={list.list_id}>
                <span
                  onClick={() => handleSelectTestList(list)}
                  className={selected?.list_id === list.list_id ? 'font-medium' : ''}>
                  {list.list_name}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <InfoAlert>
          No test lists found. Please create a test contact list first.
        </InfoAlert>
      )}
    </div>
  );
};

export default TestListSelector;
